import {type ICommandBarItemProps } from "@fluentui/react";
import {v4 as uuidv4} from "uuid";
import {type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import {type RootState } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import useConstructionNewHook from "../../../hooks/useConstructionNewHook";
import { getOrderByCreatedDate } from "../../../utils/DateFormatting";
import { useAuth } from "../../../services/auth/hooks/useAuth";
import { deleteConstructions, load, update } from "../constructionsNewSlice";
import { markAsRead } from "../../layout/messagesSlice";
import { useNavigate } from "react-router-dom";
import useCalculationHook from "../../../hooks/useCalculationNewHook";
import useTagHook from "../../../hooks/useTagNewHook";
import PathConstants from "../../../routes/pathConstants";
import useConstructionsNewCommandBarItems from "./useConstructionsNewCommandBarItems";
import { MessageType } from "../../../services/signalR/models/Messages";
import ConstructionStatus from "../../../types/ConstructionStatus";
import type ConstructionListItem from "../types/ConstructionListItem";

const useConstructionsNewPageHook = (): {
    tagModalType: string | undefined;
    isTagDialogHidden: boolean;
    isEditModalOpen: boolean;
    isDeleteConfirmationOpen: boolean;
    commandBarItems: ICommandBarItemProps[];
    setTagDialogHidden: Dispatch<SetStateAction<boolean>>;
    handleDeleteConstructions: () => void;
    handleDeleteConfirmationDismiss: () => void;
    handleBulkEditDismiss: () => void;
} => {
    const [tagModalType, setTagModalType] = useState<string | undefined>();
    const [isTagDialogHidden, setTagDialogHidden] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

    const {page, filters} = useSelector((state: RootState) => state.constructionsNew);
    const {messages} = useSelector((state: RootState) => state.signalRMessages);
    const {user} = useAuth();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {getAllConstructions, deleteConstructionById} = useConstructionNewHook();
    const {deleteCalculationByConstructionId, getLatestCalculationByConstructionId} = useCalculationHook();
    const {getAllTags} = useTagHook();
    
    const filtersRef = useRef(filters);
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    const loadFunctionsRef = useRef({getAllConstructions, getAllTags});
    useEffect(() => {
        loadFunctionsRef.current = {getAllConstructions, getAllTags};
    });

    const handleDeleteConfirmationOpenState = (): void => {
      setIsDeleteConfirmationOpen(prevState => !prevState);
    };

    const handleTagDialogDismiss = (type: string): void => {
    setTagModalType(type);
    setTagDialogHidden(prev => !prev);
  };

  const handleCalculationNavigationTrigger = (constructionId: string): void => {
    void (async () => {
      const latestCalculation =
        await getLatestCalculationByConstructionId(constructionId);
      void navigate(
        `${PathConstants.Constructions}/${constructionId}/${PathConstants.Calculations}/${latestCalculation?.data?.calculationId}`,
      );
    })();
  };

  const handleBulkEditDismiss = (): void => {
    setIsEditModalOpen(prevIsEditModalOpen => !prevIsEditModalOpen);
  };

  const getCommandBarItems = useConstructionsNewCommandBarItems(handleTagDialogDismiss, handleDeleteConfirmationOpenState, handleCalculationNavigationTrigger, handleBulkEditDismiss);
  const commandBarItems = useMemo(() => getCommandBarItems((page).filter(c => c.isSelected)), [getCommandBarItems, page]);

  const processedMessageIdsRef = useRef(new Set<string>());

  useEffect(() => {
    const relatedMessages = messages.filter(
      m => !m.isRead && m.user === user?.userName && !processedMessageIdsRef.current.has(m.id),
    );
    if (relatedMessages.length === 0) return;

    relatedMessages.forEach(message => {
      processedMessageIdsRef.current.add(message.id);
      dispatch(markAsRead(message.id));

      if (message.type === MessageType.ConstructionAccepted || message.type === MessageType.ConstructionUnaccepted) {
        if (message.data?.constructionId)
          dispatch(
            update([
              {
                id: message.data?.constructionId,
                status: message.type === MessageType.ConstructionAccepted ? ConstructionStatus.Accepted : ConstructionStatus.Registered,
              },
            ]),
          );
        return;
      }

      if (message.type === MessageType.ConstructionDeleted)
        if (message.data?.constructionId)
          dispatch(deleteConstructions([message.data?.constructionId]));
    });
  }, [dispatch, messages, user?.userName]);


  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const {getAllConstructions: getAll, getAllTags: getTags} = loadFunctionsRef.current;
      const response = await getAll();
      
      if (response?.data !== null) {
        const data = getOrderByCreatedDate(response.data);
        const currentFilters = filtersRef.current;

        const shouldAddUserNameToFilters =
          user?.isAllowedToTakeAction() && currentFilters.createdBy === undefined;

        const tagsResponse = await getTags();

        dispatch(
          load({
            data,
            filters: shouldAddUserNameToFilters
              ? {
                  ...currentFilters,
                  createdBy: user?.userName ? [user?.userName]: undefined,
                }
              : currentFilters,
            tags: tagsResponse?.data ?? [],
          }),
        );
      } else {
        dispatch(load({data: [], filters: filtersRef.current, tags: []}));
      }
    };
  
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.userName]);

  const handleDeleteConstructions = (): void => {
    handleDeleteConfirmationOpenState();
    const items = page.filter(c => c.isSelected);

    void Promise.all(
      items.map(async (item: ConstructionListItem) => {
        const correlationId = uuidv4();

        await deleteCalculationByConstructionId(item.id, correlationId);
        await deleteConstructionById(item.id, correlationId);
      }),
    );
  };

  return {
    tagModalType,
    isTagDialogHidden,
    isDeleteConfirmationOpen,
    isEditModalOpen,
    commandBarItems,
    setTagDialogHidden,
    handleDeleteConstructions,
    handleDeleteConfirmationDismiss: handleDeleteConfirmationOpenState,
    handleBulkEditDismiss,
  };
};

export default useConstructionsNewPageHook;